import React from 'react';
import { useTranslation } from 'react-i18next';
import { Disposition } from '../../types';
import { SapContact } from '../../services/sap-cti.service';
import formStyles from './ACWForm.module.css';
import layoutStyles from './ACWTimer.module.css';

interface AcwDispositionFormProps {
  dispositions: Disposition[];
  isLoadingDispositions: boolean;
  dispositionError: string | null;
  selectedDispositionId: string;
  onDispositionSelect: (dispositionId: string) => void;
  openedSapContacts: SapContact[];
  selectedSapContactId: string;
  onSapContactSelect: (contactId: string) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  requiresNotes: boolean;
}

export function AcwDispositionForm({
  dispositions,
  isLoadingDispositions,
  dispositionError,
  selectedDispositionId,
  onDispositionSelect,
  openedSapContacts,
  selectedSapContactId,
  onSapContactSelect,
  notes,
  onNotesChange,
  requiresNotes,
}: AcwDispositionFormProps) {
  const { t } = useTranslation();

  if (isLoadingDispositions) {
    return (
      <div className={formStyles.loadingSection}>
        <div className={formStyles.spinner} />
        <span>{t('acw:disposition.loadingDispositions')}</span>
      </div>
    );
  }

  return (
    <>
      {dispositions.length > 0 && (
        <div className={formStyles.dispositionSection}>
          <label className={formStyles.sectionLabel}>{t('acw:disposition.label')}</label>
          <div className={formStyles.dispositionList}>
            {dispositions.map((disposition) => (
              <button
                key={disposition.dispositionId}
                className={`${formStyles.dispositionItem} ${
                  selectedDispositionId === disposition.dispositionId ? formStyles.selected : ''
                }`}
                onClick={() => onDispositionSelect(disposition.dispositionId)}
              >
                <span className={formStyles.radio}>
                  {selectedDispositionId === disposition.dispositionId && (
                    <span className={formStyles.radioInner} />
                  )}
                </span>
                <span className={formStyles.dispositionName}>{disposition.dispositionName}</span>
              </button>
            ))}
          </div>
          {dispositionError && <p className={layoutStyles.errorText}>{dispositionError}</p>}
        </div>
      )}

      {openedSapContacts.length > 0 && (
        <div className={formStyles.sapSection}>
          <label className={formStyles.sectionLabel}>{t('acw:disposition.sapContact')}</label>
          {openedSapContacts.length === 1 ? (
            <div className={formStyles.sapContactReadonly}>{openedSapContacts[0].name}</div>
          ) : (
            <select
              className={formStyles.sapContactSelect}
              value={selectedSapContactId}
              onChange={(e) => onSapContactSelect(e.target.value)}
            >
              <option value="">{t('acw:disposition.selectContact')}</option>
              {openedSapContacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className={formStyles.notesSection}>
        <label className={formStyles.notesLabel}>
          {t('common:labels.notes')}
          {requiresNotes && (
            <span className={formStyles.required}> {t('common:labels.required')}</span>
          )}
        </label>
        <textarea
          className={formStyles.notesTextarea}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder={t('acw:disposition.notesPlaceholder')}
          rows={2}
        />
      </div>
    </>
  );
}

export default AcwDispositionForm;
